import { Fragment, useRef, type MouseEvent, type ReactNode } from 'react';
import { flexRender, type ColumnDef, type Row, type RowData, type Table as TableType } from '@tanstack/react-table';
import { SearchX } from 'lucide-react';
import { EmptyState, type EmptyStateProps } from '@/components/shared/empty-state';
import { useFillViewportHeight } from '@/hooks/use-fill-viewport-height';

// Opt-in per-column metadata. `sticky: 'right'` pins a column to the right edge
// so it stays visible while the rest of the table scrolls horizontally (e.g. an
// Actions column that would otherwise sit off-screen). See the All Products
// table for a usage example.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    sticky?: 'right';
  }
}

// Shared sticky-cell classes (background keeps the pinned cell opaque so the
// scrolling columns don't show through; the left border + shadow separate it).
//
// Sticky lives on the <th> cells rather than on <thead>: a background painted
// on a table *section* is not reliably drawn above sticky content, which lets
// rows ghost through the frozen header. Per-cell backgrounds have no such
// quirk. A cell can stick on both axes at once, which is what the corner cell
// (frozen header ∩ pinned Actions column) needs, so the z-index is picked once
// per case below instead of letting two z- utilities collide.
//
// The right-pin only applies from `sm` up. On a phone a pinned Actions column
// covers most of the viewport and hides the content it's meant to sit beside,
// so below `sm` it's an ordinary trailing column: the table scrolls and the
// actions are reached at its end. The frozen header (top-0) is unaffected and
// stays frozen at every width.
const STICKY_RIGHT_DECOR = 'sm:border-l sm:border-border sm:shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]';
const STICKY_RIGHT_TH = `sm:sticky sm:right-0 z-30 bg-gray-50 ${STICKY_RIGHT_DECOR}`;
const STICKY_RIGHT_TH_FROZEN = `sticky top-0 sm:right-0 z-30 bg-gray-50 ${STICKY_RIGHT_DECOR}`;
const FROZEN_TH = 'sticky top-0 z-20';
const STICKY_RIGHT_TD = `sm:sticky sm:right-0 z-10 bg-white group-hover:bg-gray-50 ${STICKY_RIGHT_DECOR}`;

interface DataTableProps<TData> {
  table: TableType<TData>;
  columns: ColumnDef<TData, any>[];
  /**
   * Optional expanded-row renderer. When provided, each expanded row
   * (`row.getIsExpanded() === true`) renders an extra `<tr>` below with the
   * returned content spanning all columns. Pair with TanStack's
   * `getExpandedRowModel()` + an expander cell on the parent row.
   */
  renderSubRow?: (row: Row<TData>) => ReactNode;
  /**
   * Exposes the horizontally-scrolling wrapper so a caller can drive it from
   * outside — e.g. the All Products page, which mirrors this element's scroll
   * position onto a sticky scrollbar pinned to the bottom of the viewport.
   */
  /**
   * Bounds the table to the height left between its top edge and the bottom of
   * the window, so the rows scroll inside it instead of extending the page. The
   * header freezes and, crucially, the horizontal scrollbar stays on screen
   * rather than sitting at the far end of a long page. Pass the pixels to leave
   * free underneath (a pinned pagination bar, say).
   */
  fillViewport?: { reserve: number; min?: number };
  /**
   * What the table says when it has no rows. Defaults to a generic "No results
   * found" — pass page-specific copy (and, where the list is simply empty
   * rather than filtered down, a note on how to add the first entry).
   */
  empty?: Omit<EmptyStateProps, 'className'>;
  /**
   * Makes every body row clickable (pointer cursor + handler). Clicks that land
   * on an interactive element inside the row — a button, link, input, or
   * anything inside `[data-row-click-ignore]` — are left alone so per-cell
   * actions (toggle PO, inline price edit, popover triggers) never also fire
   * the row navigation. A click that ends a text selection is ignored too.
   */
  onRowClick?: (row: Row<TData>, event: MouseEvent<HTMLTableRowElement>) => void;
}

// Elements whose own click handling must win over the row-level handler.
const ROW_CLICK_IGNORE_SELECTOR =
  'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="menuitem"], [role="checkbox"], [contenteditable="true"], [data-row-click-ignore]';

function isInteractiveClick(event: MouseEvent<HTMLTableRowElement>): boolean {
  const target = event.target as Element | null;
  if (!target || typeof target.closest !== 'function') return false;
  const hit = target.closest(ROW_CLICK_IGNORE_SELECTOR);
  return !!hit && event.currentTarget.contains(hit);
}

const DEFAULT_EMPTY: Omit<EmptyStateProps, 'className'> = {
  icon: SearchX,
  title: 'No results found',
  description: 'Try adjusting your search or filters.',
};

export function DataTable<TData>({
  table,
  columns,
  renderSubRow,
  fillViewport,
  empty = DEFAULT_EMPTY,
  onRowClick,
}: DataTableProps<TData>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const reserve = fillViewport?.reserve;
  const maxHeight = useFillViewportHeight(scrollRef, reserve, fillViewport?.min);

  return (
    <div
      ref={scrollRef}
      style={maxHeight ? { maxHeight } : undefined}
      className={
        'rounded-lg border border-border bg-white [-webkit-overflow-scrolling:touch]' +
        (reserve === undefined ? ' overflow-x-auto' : ' table-scroll overflow-auto')
      }
    >
      <table className="w-full min-w-[600px]">
        <thead className="bg-gray-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-border">
              {headerGroup.headers.map((header) => {
                const stickyRight = header.column.columnDef.meta?.sticky === 'right';
                const frozen = reserve !== undefined;
                const stickyCls = stickyRight
                  ? frozen
                    ? STICKY_RIGHT_TH_FROZEN
                    : STICKY_RIGHT_TH
                  : frozen
                    ? FROZEN_TH
                    : '';
                return (
                  <th
                    key={header.id}
                    className={
                      'bg-gray-50 px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase' +
                      (stickyCls ? ` ${stickyCls}` : '')
                    }
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  className={
                    'group border-b border-border last:border-0 hover:bg-muted/50 transition-colors' +
                    (onRowClick ? ' cursor-pointer' : '')
                  }
                  onClick={
                    onRowClick
                      ? (event) => {
                          if (isInteractiveClick(event)) return;
                          // Don't hijack a drag-to-select of cell text.
                          if (window.getSelection?.()?.toString()) return;
                          onRowClick(row, event);
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const sticky = cell.column.columnDef.meta?.sticky === 'right';
                    return (
                      <td
                        key={cell.id}
                        className={'px-4 py-3 text-sm text-foreground' + (sticky ? ` ${STICKY_RIGHT_TD}` : '')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
                {renderSubRow && row.getIsExpanded() && (
                  <tr className="border-b border-border last:border-0">
                    <td colSpan={row.getVisibleCells().length} className="bg-muted/20 px-6 py-4">
                      {renderSubRow(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
